import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  fetchEmployeeChanges, 
  saveNotificationLog, 
  formatChangesForEmail,
  GroupedChanges 
} from "./useEmployeeChanges";

const NOTIFICATION_SETTINGS_KEY = "employee_notification_settings";

export interface NotificationSettings {
  enabled: boolean;
  categories: {
    stammdaten: boolean;
    gehalt: boolean;
    bonus: boolean;
    benefits: boolean;
    stellenbeschreibung: boolean;
    kfz_kosten: boolean;
    dokumente: boolean;
    zugangsdaten: boolean;
    stunden_abwesenheit: boolean;
    arbeitskleidung: boolean;
    werkzeug: boolean;
    sonstige_kosten: boolean;
    bewertung: boolean;
    mitarbeitergespraech: boolean;
    annehmlichkeiten: boolean;
    handy: boolean;
  };
}

const defaultSettings: NotificationSettings = {
  enabled: false,
  categories: {
    stammdaten: true,
    gehalt: true,
    bonus: true,
    benefits: true,
    stellenbeschreibung: true,
    kfz_kosten: true,
    dokumente: true,
    zugangsdaten: true,
    stunden_abwesenheit: true,
    arbeitskleidung: true,
    werkzeug: true,
    sonstige_kosten: true,
    bewertung: true,
    mitarbeitergespraech: true,
    annehmlichkeiten: true,
    handy: true,
  },
};

export const getNotificationSettings = (): NotificationSettings => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error loading notification settings:", error);
  }
  return defaultSettings;
};

export const saveNotificationSettings = (settings: NotificationSettings): void => {
  try {
    localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error("Error saving notification settings:", error);
  }
};

export const sendEmployeeNotificationWithChanges = async (
  employeeId: string,
  changedByName: string,
  groupedChanges: GroupedChanges
): Promise<{ success: boolean; message: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke("notify-employee-change", {
      body: {
        employeeId,
        changedBy: changedByName,
        groupedChanges,
      },
    });

    if (error) {
      console.error("Error sending notification:", error);
      return { success: false, message: error.message };
    }

    if (data?.skipped) {
      return { success: true, message: data.error || "Übersprungen" };
    }

    // Log speichern nach erfolgreicher Benachrichtigung
    await saveNotificationLog(employeeId, changedByName, groupedChanges);

    return { success: true, message: "Benachrichtigung gesendet" };
  } catch (error: any) {
    console.error("Error sending notification:", error);
    return { success: false, message: error.message };
  }
};

export const useEmployeeNotification = () => {
  /**
   * Prüft auf Änderungen und sendet eine detaillierte Benachrichtigung
   */
  const notifyEmployeeWithChanges = async (
    employeeId: string,
    changedByName: string,
    showToast: boolean = true
  ): Promise<{ success: boolean; message: string; hasChanges: boolean }> => {
    // Änderungen seit der letzten Benachrichtigung abrufen
    const { grouped, hasChanges } = await fetchEmployeeChanges(employeeId);

    if (!hasChanges) {
      if (showToast) {
        toast.info("Keine neuen Änderungen seit der letzten Benachrichtigung vorhanden.");
      }
      return { success: true, message: "Keine neuen Änderungen", hasChanges: false };
    }

    // Benachrichtigung mit detaillierten Änderungen senden
    const result = await sendEmployeeNotificationWithChanges(employeeId, changedByName, grouped);

    if (showToast) {
      if (result.success) {
        if (result.message === "Übersprungen") {
          toast.info(result.message);
        } else {
          toast.success(result.message);
        }
      } else {
        toast.error(`Benachrichtigung fehlgeschlagen: ${result.message}`);
      }
    }

    return { ...result, hasChanges: true };
  };

  // Legacy-Funktion für Abwärtskompatibilität
  const notifyEmployeeManual = async (
    employeeId: string,
    changeCategory: keyof NotificationSettings["categories"],
    changedByName: string,
    changeDetails?: string
  ) => {
    return notifyEmployeeWithChanges(employeeId, changedByName, true);
  };

  return {
    notifyEmployeeWithChanges,
    notifyEmployeeManual,
    getSettings: getNotificationSettings,
    saveSettings: saveNotificationSettings,
    fetchChanges: fetchEmployeeChanges,
  };
};
